// `/approvazione` — read-only landing page for intermediary users whose
// onboarding has not (yet) granted operational access.
//
// Why it lives outside `<AppShell>`:
//   The operational sidebar/header would advertise routes the user
//   cannot reach. A minimal, centered shell makes the temporary nature
//   of the page obvious and avoids tempting menu items.
//
// State machine:
//   loading            → centered spinner
//   error              → ErrorState + retry
//   ACTIVE             → <Navigate to="/" replace /> (gate handles loop avoidance)
//   PENDING_APPROVAL   → "Richiesta in approvazione" + read-only summary
//   REJECTED           → "Richiesta non approvata" + read-only summary
//   NO_MEMBERSHIP      → "Accesso non ancora disponibile"
//
// CTA policy (locked-in by user feedback during PR-AP2):
//   Only "Aggiorna stato" (refetch). NO operational CTAs on REJECTED /
//   NO_MEMBERSHIP — those statuses must not surface any path back into
//   the operational app.

import { Navigate } from 'react-router-dom';
import { GlassCard } from '@ab-platform/ui/glass-card';
import { SoftBadge } from '@ab-platform/ui/soft-badge';
import { PillButton } from '@ab-platform/ui/pill-button';
import { DataList, DataListRow } from '@ab-platform/ui/data-list';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { LoadingState } from '@/components/shared/LoadingState';
import { ErrorState } from '@/components/shared/ErrorState';
import { formatDateTime } from '@/lib/format';
import { useApprovalState } from '../hooks/useApprovalState';
import { STATUS_LABEL_IT } from '../lib/approval-routing';
import type { ApprovalStateStatus } from '../types';

interface PresentationConfig {
  title: string;
  body: string;
  badgeTone: 'warning' | 'danger' | 'neutral';
}

// Per-status copy + badge tone. Kept here (not in approval-routing) because
// these strings are page-presentational, not routing logic — moving them
// would muddle the helper's responsibility.
const PRESENTATION: Record<
  Exclude<ApprovalStateStatus, 'ACTIVE'>,
  PresentationConfig
> = {
  PENDING_APPROVAL: {
    title: 'Richiesta in approvazione',
    body: 'La tua richiesta di censimento è stata ricevuta ed è in fase di verifica da parte di Aurino Broker.',
    badgeTone: 'warning',
  },
  REJECTED: {
    title: 'Richiesta non approvata',
    body: 'La richiesta non è stata approvata. Per maggiori informazioni contatta Aurino Broker.',
    badgeTone: 'danger',
  },
  NO_MEMBERSHIP: {
    title: 'Accesso non ancora disponibile',
    body: 'Il tuo account non risulta associato a una pratica o a un’organizzazione operativa.',
    badgeTone: 'neutral',
  },
};

export function ApprovalPage(): JSX.Element {
  const query = useApprovalState();

  if (query.isLoading || query.isPending) {
    return (
      <FocusedPageShell title="Stato richiesta">
        <LoadingState message="Verifica dello stato della richiesta…" />
      </FocusedPageShell>
    );
  }

  if (query.isError) {
    return (
      <FocusedPageShell title="Stato richiesta">
        <ErrorState
          error={
            query.error instanceof Error
              ? new ErrorWithFriendlyMessage(query.error)
              : query.error
          }
          onRetry={() => void query.refetch()}
        />
      </FocusedPageShell>
    );
  }

  const data = query.data;
  if (data.status === 'ACTIVE') {
    // Membership active — the user belongs in the operational app. The
    // ApprovalGate normally prevents reaching this page when ACTIVE, but
    // direct URL hits are valid and route the user home.
    return <Navigate to="/" replace />;
  }

  const config = PRESENTATION[data.status];

  return (
    <FocusedPageShell title={config.title} description={config.body}>
      <GlassCard className="p-6">
        <DataList>
          <DataListRow
            label="Stato richiesta"
            value={
              <SoftBadge tone={config.badgeTone}>
                {STATUS_LABEL_IT[data.status]}
              </SoftBadge>
            }
            showEmpty
          />
          <DataListRow label="Organizzazione" value={data.organizationName} />
          <DataListRow label="Tipo organizzazione" value={data.orgType} />
          <DataListRow
            label="Data richiesta"
            value={data.requestedAt ? formatDateTime(data.requestedAt) : null}
          />
          {data.status === 'REJECTED' ? (
            <DataListRow
              label="Data esito"
              value={data.rejectedAt ? formatDateTime(data.rejectedAt) : null}
            />
          ) : null}
        </DataList>
      </GlassCard>

      <div className="mt-6 flex justify-center">
        <PillButton
          variant="ghost"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
        >
          {query.isFetching ? 'Aggiornamento…' : 'Aggiorna stato'}
        </PillButton>
      </div>
    </FocusedPageShell>
  );
}

// ApiError already exposes a `code` + `message` — but on transient network
// failures the message is e.g. "Failed to fetch", which leaks impl detail.
// The ErrorState component shows `message` verbatim, so we wrap with a
// human-friendly Italian message here while preserving the original code
// stack via `cause`.
class ErrorWithFriendlyMessage extends Error {
  constructor(original: Error) {
    super(
      'Non siamo riusciti a recuperare lo stato della tua richiesta. Riprova tra qualche minuto.',
    );
    this.name = original.name;
    if ('code' in original) {
      Object.assign(this, { code: (original as { code: unknown }).code });
    }
    this.cause = original;
  }
}

export default ApprovalPage;
